import { installChargingStatisticsRuntime } from './charging-statistics-runtime'
import { syncChartMotion, stopChartMotion } from './chart-motion'
import { supportsEchartsOption, validateEchartsOption, composeEchartsOption } from '@/editor/core/echarts-option'
import { composeGaugePresentation, gaugeAccessibleLabel } from './gauge-presentation'
import { gaugeDisplayData, hasGaugeDataOverrides } from './gauge-data'
import { composeLinePresentation, lineAccessibleLabel, fitLineAxisName, fitLinePlotPadding, fitLineValueLabels } from './line-presentation'
import { composeBarPresentation, composeBarValueLabels, fitBarLabelPadding } from './bar-presentation'
import { composeCartesianLayout } from './chart-layout'
import { composeBarRank, barRankAccessibleLabel, isBarRankStyle } from './bar-rank'
import { composeLineXAxis } from './line-xaxis'
import { composeChartVisibility } from './chart-visibility'

const states = new WeakMap()
const kindOf = vm => (vm.$options.name || '').replace(/^avue-echart-/, '')
const labels = new WeakMap()

function presentationLabel (vm, active, label) {
  const element = vm.$el
  if (!element || !element.setAttribute) return
  if (active) {
    if (!labels.has(vm)) labels.set(vm, { role: element.getAttribute('role'), label: element.getAttribute('aria-label') })
    element.setAttribute('role', 'img'); element.setAttribute('aria-label', label || gaugeAccessibleLabel(vm.dataChart, vm.option))
  } else if (labels.has(vm)) {
    const previous = labels.get(vm)
    for (const [key, value] of [['role', previous.role], ['aria-label', previous.label]]) {
      if (value === null) element.removeAttribute(key); else element.setAttribute(key, value)
    }
    labels.delete(vm)
  }
}

// 旧版仪表把空字符串渲染成 NaN；页面待传参/待首值时保留刻度，不伪造零值或指针。
function pageGaugePlaceholder (option, data) {
  const unit = typeof data.unit === 'string' ? data.unit.replace(/[{}\r\n]/g, '') : ''
  return { ...option, series: (option.series || []).map(series => ({ ...series,
    pointer: { ...series.pointer, show: false },
    detail: { ...series.detail, formatter: () => '—' + (unit ? ' ' + unit : '') },
    data: (series.data && series.data.length ? series.data : [{}]).map(datum => {
      const item = datum && typeof datum === 'object' ? { ...datum } : {}
      delete item.value
      return item
    })
  })) }
}

export function renderWithEchartsOption (vm, original, args) {
  const chart = vm.myChart
  if (!chart || !chart.setOption) return original.apply(vm, args)
  const raw = vm.option && vm.option.echartsOption
  const gauge = kindOf(vm) === 'gauge'
  const overrides = gauge && hasGaugeDataOverrides(vm.option)
  const display = gauge ? gaugeDisplayData(vm.dataChart, vm.option) : vm.dataChart
  const tech = gauge && vm.option && vm.option.gaugeStyle === 'tech'
  const lineTech = kindOf(vm) === 'line' && vm.option && vm.option.linePreset === 'tech'
  const namedLine = kindOf(vm) === 'line' && vm.option && !!vm.option.yAxisName
  const bar = kindOf(vm) === 'bar', cartesian = bar || kindOf(vm) === 'line'
  const barRank = bar && isBarRankStyle(vm.option)
  const unit = vm.option && vm.option.yAxisName || vm.parameter && vm.parameter.propertyUnit || ''
  const value = vm.dataChart && vm.dataChart.value
  const placeholder = !tech && kindOf(vm) === 'gauge' && Number(vm.dataType) === 2 &&
    vm.parameter && vm.parameter.deviceSource === 'page' &&
    (value === undefined || value === null || (typeof value === 'string' && !value.trim()))
  presentationLabel(vm, tech || placeholder || lineTech || barRank, barRank ? barRankAccessibleLabel(vm.dataChart, vm.option) : lineTech ? lineAccessibleLabel(vm.dataChart, vm.option, unit) : '')
  if (raw === undefined && !tech && !lineTech && !namedLine && !cartesian && !placeholder && !overrides) { states.delete(vm); return original.apply(vm, args) }
  let state = states.get(vm)
  if (!state) { state = {}; states.set(vm, state) }
  const setOption = chart.setOption
  chart.setOption = function (generated, ...rest) {
    const reduced = (tech || lineTech || barRank) && typeof window !== 'undefined' && window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches
    const size = {
      width: chart.getWidth ? chart.getWidth() : vm.width, height: chart.getHeight ? chart.getHeight() : vm.height,
      animation: vm.animation !== false && !reduced
    }
    const paint = (option, custom = {}) => {
      const engine = typeof window !== 'undefined' && window.echarts
      if (barRank) return setOption.call(chart, composeBarRank(option, vm.option, size, engine), ...rest)
      const visible = cartesian ? composeChartVisibility(option, vm.option) : option
      let fitted = bar ? composeBarPresentation(composeBarValueLabels(visible, custom), vm.option) : visible
      if (kindOf(vm) === 'line') fitted = composeLineXAxis(fitted, vm.option, custom)
      if (cartesian) fitted = composeCartesianLayout(fitted, vm.option, engine, custom, size)
      if (kindOf(vm) === 'line') { fitted = fitLineValueLabels(fitted, size, engine); fitted = fitLineAxisName(fitted, size) }
      if (bar) fitted = fitBarLabelPadding(fitted, size, typeof window !== 'undefined' && window.echarts)
      if (lineTech) fitted = fitLinePlotPadding(fitted, size, typeof window !== 'undefined' && window.echarts)
      return setOption.call(chart, placeholder ? pageGaugePlaceholder(fitted, display || {}) : fitted, ...rest)
    }
    const styled = lineTech ? composeLinePresentation(generated, vm.option, unit) : tech || overrides ? composeGaugePresentation(generated, vm.option, vm.dataChart, size) : generated
    const draw = custom => {
      const dynamic = Number(vm.dataType || 0) !== 0
      let base = styled
      // 静态 Option 明确提供的数据仍可覆盖示例；动态仪表始终使用本轮真实读数和量程。
      if (tech && !dynamic && custom.series && custom.series[0]) {
        const series = custom.series[0], datum = series.data && series.data[0]
        const data = { ...vm.dataChart }
        for (const key of ['min', 'max']) if (series[key] !== undefined) data[key] = series[key]
        if (datum !== undefined) data.value = datum && typeof datum === 'object' ? datum.value : datum
        base = composeGaugePresentation(generated, vm.option, data, size)
      }
      const option = composeEchartsOption(base, custom, dynamic, tech || lineTech || placeholder)
      if ((tech || placeholder) && dynamic) option.series.forEach((series, i) => {
        const actual = base.series[i] || base.series[0]
        series.min = actual.min; series.max = actual.max
      })
      if (overrides) option.series.forEach((series, i) => {
        const actual = base.series[i] || base.series[0]
        if (vm.option.gaugeMin !== undefined) series.min = actual.min
        if (vm.option.gaugeMax !== undefined) series.max = actual.max
        if (typeof vm.option.gaugeLabel === 'string') {
          series.name = actual.name
          series.title = { ...series.title, show: actual.title && actual.title.show }
          series.data = (series.data || []).map((datum, at) => ({ ...(datum && typeof datum === 'object' ? datum : { value: datum }), name: (actual.data[at] || actual.data[0] || {}).name }))
        }
      })
      if (vm.animation === false || reduced) {
        option.animation = false; option.animationDuration = 0; option.animationDurationUpdate = 0
        if (option.series) option.series.forEach(series => { series.animation = false; series.animationDuration = 0; series.animationDurationUpdate = 0 })
      }
      return paint(option, custom)
    }
    if (raw === undefined) { states.delete(vm); return draw({}) }
    try {
      if (state.raw !== raw) { state.parsed = validateEchartsOption(raw, kindOf(vm)); state.raw = raw }
      const result = draw(state.parsed)
      state.valid = state.parsed
      vm.$emit('echarts-option-rendered')
      return result
    } catch (error) {
      vm.$emit('echarts-option-error', error)
      // 不把配置错误向外抛至数据订阅；继续用当前数据和上一份有效样式。
      return state.valid ? draw(state.valid) : paint(styled)
    }
  }
  try { return original.apply(vm, args) } finally { chart.setOption = setOption }
}

// 扩展项目已有的 Avue ECharts 实例，不注册图表类型、不改动压缩版第三方库。
export function installEchartsOptionRuntime (Vue) {
  if (Vue.__echartsOptionRuntime) return
  Vue.__echartsOptionRuntime = true
  installChargingStatisticsRuntime(Vue)
  Vue.mixin({
    beforeCreate () {
      if (!(this.$options.name || '').startsWith('avue-echart-') || !supportsEchartsOption(kindOf(this))) return
      const original = this.$options.methods && this.$options.methods.updateChart
      if (!original) return
      this.$options.methods = { ...this.$options.methods, updateChart (...args) { const result = renderWithEchartsOption(this, original, args); syncChartMotion(this); return result } }
    },
    beforeDestroy () { stopChartMotion(this); presentationLabel(this, false); states.delete(this) }
  })
}
